import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import DropTarget from '@uppy/drop-target';
import Logger from '@medflex/utils/Logger';
import { ALLOWED_MIME_TYPES } from '@medflex/utils/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import localeDe from '@uppy/locales/lib/de_DE';
import XHR from '@uppy/xhr-upload';

const useUppy = ({
  id: uppyId,
  multiple = false,
  endpoint = '',
  meta = {},
  headers,
  restrictions = {},
  onRestrictionFailed,
  dropTarget = null,
  localeOverrides,
  uploader = 'tus',
} = {}) => {
  const extendedRestrictions = {
    maxTotalFileSize: 800 * 1024 * 1024, // Default maximum file size is 800 MB
    allowedFileTypes: ALLOWED_MIME_TYPES,
    ...restrictions,
  };

  const locale = {
    ...localeDe,
    strings: {
      ...localeDe.strings,
      youCanOnlyUploadFileTypes: 'Dieses Dateiformat wird nicht unterstützt.',
      ...localeOverrides,
    },
  };

  const getResponseData = useCallback(response => {
    const responseData = JSON.parse(response);
    if (Array.isArray(responseData) && responseData.length > 0) {
      return { url: responseData[0].key };
    }
    return responseData;
  }, []);

  const uppy = useRef(new Uppy({ id: uppyId, restrictions: extendedRestrictions, locale }));

  useEffect(() => {
    if (uploader === 'tus' && !uppy.current.getPlugin('Tus')) {
      const unusedPlugin = uppy.current.getPlugin('XHRUpload');
      if (unusedPlugin) {
        uppy.current.removePlugin(unusedPlugin);
      }

      uppy.current.use(Tus, {
        id: 'Tus',
        endpoint,
        chunkSize: 6 * 1024 * 1024, // It is important that the chunk size is the same like in the backend
        limit: 1,
        headers,
        removeFingerprintOnSuccess: true,
      });
    } else {
      const unusedPlugin = uppy.current.getPlugin('Tus');
      if (unusedPlugin) {
        uppy.current.removePlugin(unusedPlugin);
      }

      uppy.current.use(XHR, {
        id: 'XHRUpload',
        endpoint,
        headers,
        formData: true,
        bundle: true,
        fieldName: 'files',
        getResponseData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploader]);

  const inputRef = useRef();
  const [files, setFiles] = useState([]);
  const [lastAddedFilesIds, setLastAddedFilesIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);

  const removeFile = file => {
    uppy.current.removeFile(file.id);
    setUploading(false);
  };

  const addBlob = (blob, { source = 'Local', name, type: typeRaw, meta: metaOverrides } = {}) => {
    const type = typeRaw ? typeRaw.replace('audio', 'video') : blob.type;
    try {
      uppy.current.addFile({
        source,
        name: name || new Date().toISOString(),
        // mime type detectors don't work with audio
        type: type ? type.replace('audio', 'video') : blob.type,
        data: blob,
        meta: {
          contentType: type,
          ...meta,
          ...metaOverrides,
        },
      });
    } catch (err) {
      if (err.isRestriction) {
        Logger.info('Restriction error:', err);
      } else {
        Logger.info(err);
      }
    }
  };

  const reset = useCallback(() => {
    setUploading(false);
    setErrors([]);

    if (uppy.current) {
      uppy.current.cancelAll();
    }

    if (inputRef.current) {
      inputRef.current.value = [];
    }
  }, [uppy, inputRef]);

  const upload = () => uppy.current.upload();

  const handleChange = useCallback(
    ({ target }) => {
      if (!multiple) {
        const existingFile = uppy.current.getFiles().shift();
        if (existingFile) removeFile(existingFile);
      }

      const targetFiles = Array.from(target.files);
      targetFiles.forEach(file => {
        try {
          uppy.current.addFile({
            source: 'file input',
            name: file.name,
            type: file.type,
            data: file,
            meta,
          });
        } catch (err) {
          if (err.isRestriction) {
            Logger.info('Restriction error:', err);
          } else {
            Logger.info(err);
          }
        }
      });
      // On every change we need to reset the value of the file input
      // This is needed to prevent https://medflexgmbh.atlassian.net/browse/MFLEX-2397
      // Otherwise, if user deletes a file and wants to add it again, it will not be possible
      // because the onChange event will not be triggered
      inputRef.current.value = [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multiple, uppy],
  );

  const retry = ({ id: fileId }) => uppy.current.retryUpload(fileId);

  const register = () => ({
    onChange: handleChange,
    ref: inputRef,
    multiple,
    accept:
      extendedRestrictions.allowedFileTypes && extendedRestrictions.allowedFileTypes.join(','),
  });

  const setOptions = opts => uppy.current.setOptions(opts);

  const handleNewFiles = items => {
    setFiles(items);
    setLastAddedFilesIds(items.map(item => item.id));
  };

  useEffect(() => {
    if (uploader === 'tus' && uppy.current.getPlugin('Tus')) {
      uppy.current.getPlugin('Tus').setOptions({ headers, endpoint });
      // This is a way to update headers during upload, since setOptions (line above) doesn't seem to do that
      // Especially relevant when auth token changes, see ticket https://medflexgmbh.atlassian.net/browse/MFLEX-2416
      // For more information on Uppy behaviour see this issue: https://github.com/transloadit/uppy/issues/3434
      if (uppy.current.getPlugin('Tus') && uppy.current.getPlugin('Tus').uploaders) {
        Object.values(uppy.current.getPlugin('Tus').uploaders).forEach(uploader => {
          const uploaderInstance = uploader;
          if (uploaderInstance) {
            uploaderInstance.options = { ...uploaderInstance.options, headers, endpoint };
          }
        });
      }
    }
  }, [headers, endpoint, uploader]);

  useEffect(() => {
    if (dropTarget && dropTarget.current && uppy.current) {
      const dropTargetPlugin = uppy.current.getPlugin('DropTarget');
      if (dropTargetPlugin) {
        dropTargetPlugin.setOptions({ target: dropTarget.current });
      } else {
        uppy.current.use(DropTarget, {
          target: dropTarget.current,
        });
      }
    }
  }, [uppy, dropTarget]);

  useEffect(() => {
    const refreshFiles = newFilesHandler => () => {
      if (multiple || uppy.current.getFiles().length < 2) {
        newFilesHandler(uppy.current.getFiles());
      } else {
        const newFile = uppy.current.getFiles().slice(-1).pop();
        reset();
        newFilesHandler([newFile]);
        uppy.current.addFile(newFile);
      }
    };

    if (uppy.current) {
      // It is important to use refreshFiles(handleNewFiles) only on files-added event
      // otherwise lastAddedFilesIds gets updated during uploads, which
      // leads to wrong files to be shown if user switches between chats during uploads
      // all other events should update files but not lastAddedFilesIds
      uppy.current.on('files-added', refreshFiles(handleNewFiles));
      uppy.current.on('file-removed', refreshFiles(setFiles));
      uppy.current.on('upload-progress', refreshFiles(setFiles));
      uppy.current.on('upload-success', refreshFiles(setFiles));
      uppy.current.on('upload', () => setUploading(true));
      uppy.current.on('complete', () => {
        refreshFiles(setFiles);
        setUploading(false);
      });
    }
  }, [uppy]);

  useEffect(() => {
    const handleError = (file, error, response) => {
      setErrors([...(errors || []), { file, error, response }]);
    };

    const refreshErrors = file => {
      setErrors(errors.filter(error => error.file.id !== file.id));
    };

    const uppyRef = uppy.current;

    if (uppyRef) {
      uppyRef.on('restriction-failed', onRestrictionFailed);
      uppyRef.on('file-removed', refreshErrors);
      uppyRef.on('upload-error', handleError);
      return () => {
        uppyRef.off('restriction-failed', onRestrictionFailed);
        uppyRef.off('file-removed', refreshErrors);
        uppyRef.off('upload-error', handleError);
      };
    }

    return undefined;
  }, [uppy, errors, onRestrictionFailed]);

  return {
    files,
    lastAddedFilesIds,
    removeFile,
    addBlob,
    upload,
    reset,
    retry,
    setOptions,
    uploading,
    errors,
    register,
  };
};

export default useUppy;
